<template>
  <div
    class="work-schedule-editor"
    :class="{ 'work-schedule-editor--dirty': isDirty }">
    <div class="work-schedule-editor__header">
      <slot name="heading">
        <h2>График рабочего времени</h2>
      </slot>
      <div class="work-schedule-editor__month">
        <a-button
          :disabled="loading || saving || periodIndex <= 0"
          aria-label="Предыдущий месяц"
          @click="selectPeriod(options[periodIndex - 1]?.value)">
          ‹
        </a-button>
        <span
          class="work-schedule-editor__period"
          aria-live="polite">
          {{ options.find((item) => item.value === period)?.label ?? 'Загрузка…' }}
        </span>
        <a-button
          :disabled="loading || saving || periodIndex >= options.length - 1"
          aria-label="Следующий месяц"
          @click="selectPeriod(options[periodIndex + 1]?.value)">
          ›
        </a-button>
      </div>
    </div>

    <a-alert
      v-if="schedule?.is_locked && schedule?.is_editable"
      class="work-schedule-editor__alert"
      type="info"
      show-icon
      message="График заблокирован для сотрудника"
      description="Вы редактируете его как руководитель — изменения согласуйте с сотрудником." />

    <div class="work-schedule-editor__layout">
      <div class="work-schedule-editor__calendar">
        <a-spin :spinning="loading">
          <work-schedule-calendar
            :key="`${employeeId ?? 'me'}:${period}`"
            v-model:days="days"
            :editable="!!schedule?.is_editable && !loading && !saving"
            :can-manage-absences="schedule?.can_manage_absences ?? false"
            :defaults="bulk"
            :start-date="schedule?.is_filled ? null : startDate" />
        </a-spin>
      </div>

      <a-card
        v-if="schedule?.is_editable"
        size="small"
        title="Быстрое заполнение"
        class="work-schedule-editor__bulk">
        <fieldset
          :disabled="loading || saving"
          class="work-schedule-editor__fields">
          <div class="work-schedule-editor__controls">
            <a-range-picker
              v-model:value="fillRange"
              value-format="YYYY-MM-DD"
              format="DD.MM.YYYY"
              :allow-clear="false"
              :disabled-date="isFillDateDisabled"
              aria-label="Диапазон заполнения" />
            <div
              v-if="bulkDayType === DAY_TYPE.WORKING"
              class="work-schedule-editor__time-range">
              <a-time-picker
                v-model:value="bulk.started_at"
                value-format="HH:mm"
                format="HH:mm"
                :minute-step="5"
                :allow-clear="false"
                :show-now="false"
                placeholder="Начало" />

              <a-time-picker
                v-model:value="bulk.ended_at"
                value-format="HH:mm"
                format="HH:mm"
                :minute-step="5"
                :allow-clear="false"
                :show-now="false"
                placeholder="Окончание" />
            </div>
            <a-select
              v-model:value="bulkDayType"
              class="work-schedule-editor__day-type"
              aria-label="Тип дня для быстрого заполнения"
              :options="bulkDayTypeOptions" />

            <a-checkbox v-model:checked="onlyEmpty">
              Только незаполненные дни
            </a-checkbox>

            <a-button
              type="primary"
              block
              :disabled="!canApply"
              @click="applyFill">
              Применить
            </a-button>
          </div>
        </fieldset>
      </a-card>
    </div>

    <div
      v-if="schedule?.is_editable && isDirty"
      class="work-schedule-editor__footer">
      <span
        v-if="isDirty"
        class="work-schedule-editor__dirty">
        Есть несохранённые изменения
      </span>      <a-button
        type="primary"
        :loading="saving"
        :disabled="loading || !isDirty || !totals.filled_days_count"
        @click="save">
        Сохранить график
      </a-button>

      <a-button
        :disabled="loading || !isDirty || saving"
        @click="reset">
        Отменить изменения
      </a-button>
    </div>
  </div>
</template>

<script setup>
import {
  computed, h, onMounted, reactive, ref, watch,
} from 'vue';
import { message, Modal } from 'ant-design-vue';
import { api } from '@/api';
import WorkScheduleCalendar from '@/Components/CRM/WorkSchedule/WorkScheduleCalendar.vue';
import {
  DAY_TYPE, DAY_TYPE_OPTIONS, isAbsence, durationMinutes,
  periodOptions, scheduleTotals, toPayload,
} from '@/Components/CRM/WorkSchedule/workScheduleFormat';

const props = defineProps({
  initialPeriod: {
    type: String,
    default: null,
  },
  employeeId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['loaded', 'saved']);

const periods = ref([]);
const period = ref(null);
const periodsLoading = ref(false);
const loading = ref(false);
const saving = ref(false);
const schedule = ref(null);
const days = ref([]);
const savedDays = ref('[]');
const startDate = ref(null);
const fillRange = ref([]);
const bulkDayType = ref(DAY_TYPE.WORKING);
const onlyEmpty = ref(true);

const bulk = reactive({ started_at: '10:00', ended_at: '19:00' });

const options = computed(() => periodOptions([...periods.value]
  .sort((left, right) => left.key.localeCompare(right.key))));
const bulkDayTypeOptions = computed(() => DAY_TYPE_OPTIONS.filter((option) => schedule.value?.can_manage_absences
  || !isAbsence({ day_type: option.value })));
const totals = computed(() => scheduleTotals(days.value));
const isDirty = computed(() => JSON.stringify(toPayload(days.value)) !== savedDays.value);

const canApply = computed(() => !loading.value && !saving.value
  && fillRange.value?.length === 2
  && fillRange.value.every(Boolean)
  && bulkDayTypeOptions.value.some((option) => option.value === bulkDayType.value)
  && (bulkDayType.value !== DAY_TYPE.WORKING
    || (!!bulk.started_at && !!bulk.ended_at && bulk.started_at !== bulk.ended_at)));

const applySchedule = (data) => {
  schedule.value = data;
  bulkDayType.value = DAY_TYPE.WORKING;
  days.value = data.days.map((day) => ({ ...day }));
  startDate.value = data.can_initially_fill && props.employeeId === null
    ? data.initial_start_date : data.days[0]?.date;
  fillRange.value = [startDate.value, data.days.at(-1)?.date];
  savedDays.value = JSON.stringify(toPayload(days.value));

  if (data.defaults?.started_at) bulk.started_at = data.defaults.started_at;
  if (data.defaults?.ended_at) bulk.ended_at = data.defaults.ended_at;

  emit('loaded', data);
};

const loadSchedule = async () => {
  if (!period.value) return;

  loading.value = true;

  try {
    const { data } = props.employeeId === null
      ? await api.getMyWorkSchedule({ period: period.value })
      : await api.getEmployeeWorkSchedule(props.employeeId, { period: period.value });

    applySchedule(data);
  } catch (e) {
    message.error(e.response?.data?.message ?? 'Не удалось загрузить график');
  } finally {
    loading.value = false;
  }
};

const loadPeriods = async () => {
  periodsLoading.value = true;

  try {
    const { data } = await api.getWorkSchedulePeriods();

    periods.value = data.items;
    period.value = data.items.find((item) => item.key === props.initialPeriod)?.key
      ?? data.items.find((item) => item.key === data.default.key)?.key
      ?? data.items[0]?.key
      ?? null;
  } catch (e) {
    message.error(e.response?.data?.message ?? 'Не удалось загрузить список месяцев');
  } finally {
    periodsLoading.value = false;
  }
};

const isStartDateDisabled = (date) => {
  const value = date.format('YYYY-MM-DD');

  return !days.value.some((day) => day.date === value)
    || (props.employeeId === null && schedule.value?.can_initially_fill
      && value < schedule.value.initial_start_date);
};

const isFillDateDisabled = isStartDateDisabled;

const applyFill = () => {
  if (!canApply.value || !schedule.value?.is_editable) return;

  const working = bulkDayType.value === DAY_TYPE.WORKING;
  const minutes = durationMinutes(bulk.started_at, bulk.ended_at);
  days.value = days.value.map((day) => {
    if (!schedule.value.can_manage_absences && isAbsence(day)) return day;
    if (onlyEmpty.value && day.is_filled) return day;
    if (day.date < fillRange.value[0] || day.date > fillRange.value[1]
      || (!schedule.value.is_filled && day.date < startDate.value)) return day;

    return {
      ...day,
      is_filled: true,
      day_type: bulkDayType.value,
      is_day_off: !working,
      started_at: working ? bulk.started_at : null,
      ended_at: working ? bulk.ended_at : null,
      duration_minutes: working ? minutes : null,
    };
  });
};

const reset = () => {
  if (schedule.value) applySchedule(schedule.value);
};

const save = async () => {
  saving.value = true;

  try {
    const payload = {
      period: period.value,
      days: toPayload(days.value),
      ...(!schedule.value.is_filled
        ? { start_date: days.value.find((day) => day.is_filled)?.date ?? startDate.value } : {}),
    };

    const { data } = props.employeeId === null
      ? await api.saveMyWorkSchedule(payload)
      : await api.saveEmployeeWorkSchedule(props.employeeId, payload);

    applySchedule(data);
    message.success('График сохранён');
    emit('saved', data);
    return true;
  } catch (e) {
    message.error(e.response?.data?.message ?? 'Не удалось сохранить график');
    return false;
  } finally {
    saving.value = false;
  }
};

const periodIndex = computed(() => options.value.findIndex((item) => item.value === period.value));
const selectPeriod = (value) => {
  if (!value || value === period.value || loading.value || saving.value) return;
  if (!isDirty.value) {
    period.value = value;
    return;
  }
  const dialog = Modal.confirm({
    title: 'Есть несохранённые изменения',
    content: 'Сохранить график перед переходом к другому месяцу?',
    okText: 'Сохранить и перейти',
    cancelText: 'Остаться',
    onOk: async () => {
      if (!await save()) throw new Error('График не сохранён');
      period.value = value;
    },
    footer: (_, { OkBtn, CancelBtn }) => [
      h('a', { onClick: () => { dialog.destroy(); reset(); period.value = value; } }, 'Не сохранять'),
      h(CancelBtn), h(OkBtn),
    ],
  });
};

watch(period, loadSchedule);
watch(() => props.employeeId, loadSchedule);

onMounted(loadPeriods);
</script>

<style lang="scss" scoped>
.work-schedule-editor {
  &--dirty { padding-bottom: 16px; }
  &__month { display: flex; align-items: center; gap: 6px; margin-left: auto; }
  &__header h2 { margin: 0; }
  &__header :deep(.ant-page-header) { padding: 0; }
  &__alert {
    margin-bottom: 16px;
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__period {
    white-space: nowrap;
    text-align: center;
  }

  &__layout {
    display: flex;
    align-items: flex-start;
    gap: 20px;
  }

  &__calendar {
    flex: 1;
    min-width: 0;
  }

  &__fields {
    min-width: 0;
    padding: 0;
    margin: 0;
    border: 0;
  }

  &__bulk {
    flex: 0 0 300px;
    min-width: 0;
    margin-bottom: 16px;
  }

  &__bulk .ant-btn {
    max-width: 100%;
    height: auto;
    white-space: normal;
  }

  &__bulk :deep(.ant-card-head-title) { text-align: center; }

  &__day-type,
  &__controls > .ant-picker { width: 100%; }

  &__day-type {
    min-width: 0;
    :deep(.ant-select-selector) { padding-inline: 30px; }
    :deep(.ant-select-selection-item) {
      padding-inline-end: 0;
      text-align: center;
    }
  }

  &__time-range {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    :deep(.ant-picker) { width: 100%; min-width: 0; }
    :deep(.ant-picker-input > input) { text-align: center; }
    :deep(.ant-picker-suffix) { position: absolute; right: 10px; }
  }

  &__controls {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;

    > * { max-width: 100%; }
  }

  &__footer {
    position: sticky;
    bottom: 16px;
    z-index: 20;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 12px 16px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
  }

  @media (max-width: 1100px) {
    &__layout { flex-direction: column; }
    &__calendar { width: 100%; }
    &__bulk { flex: auto; width: 100%; }
  }

  &__dirty {
    margin-right: auto;
    color: #d46b08;
    font-size: 12px;
  }
}
</style>
